/**
 * Studio — Process Templates list (façade org-scoped).
 */

import { useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { AppShell } from "../../layouts/AppShell";
import { Button } from "../../primitives/Button";
import { useProcessTemplates } from "../../../api/opsV2/hooks";
import { createProcessTemplateStudio } from "../../../api/opsV2/client";

type StudioProcessTemplatesPageProps = {
  userName?: string;
  onBack?: () => void;
};

export function StudioProcessTemplatesPage({
  userName = "Utilisateur",
  onBack,
}: StudioProcessTemplatesPageProps) {
  const { data: templates, loading, error, refetch } = useProcessTemplates();
  const [creating, setCreating] = useState(false);

  const handleNewTemplate = async () => {
    setCreating(true);
    try {
      const created = await createProcessTemplateStudio({ name: "Nouveau template", ops_family: null });
      window.location.hash = `#/studio/process-templates/${created.id}/editor`;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Erreur lors de la création";
      alert(msg);
    } finally {
      setCreating(false);
    }
  };

  const openEditor = (id: string) => {
    window.location.hash = `#/studio/process-templates/${id}/editor`;
  };

  return (
    <AppShell
      userName={userName}
      topbar={
        <div className="flex items-center gap-4">
          {onBack && (
            <button type="button" onClick={onBack} className="text-sm text-muted hover:text-text">
              Studio
            </button>
          )}
          <span className="text-sm font-medium text-text">Process Templates</span>
        </div>
      }
    >
      <div className="ds-container py-6">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <h1 className="text-xl font-semibold text-text">Process Templates</h1>
            <p className="text-sm text-muted mt-1">
              Modèles de processus (étapes, activités). Créez ou éditez un template dans l’éditeur visuel.
            </p>
          </div>
          <Button
            label="Nouveau template"
            intent="primary"
            size="sm"
            onClick={handleNewTemplate}
            state={creating ? "loading" : "default"}
          />
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted py-8">
            <Loader2 className="h-4 w-4 animate-spin" />
            Chargement…
          </div>
        )}
        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">
            {error.message}
            <button type="button" onClick={() => refetch()} className="ml-2 underline">
              Réessayer
            </button>
          </div>
        )}
        {!loading && !error && (!templates || templates.length === 0) && (
          <p className="text-sm text-muted py-6">Aucun Process Template. Cliquez sur « Nouveau template » pour en créer un.</p>
        )}
        {!loading && !error && templates && templates.length > 0 && (
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left p-3 font-medium text-text">Nom</th>
                  <th className="text-left p-3 font-medium text-text">Famille</th>
                  <th className="text-left p-3 font-medium text-text">Statut</th>
                  <th className="text-left p-3 w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr
                    key={t.id}
                    className="border-b border-border/50 hover:bg-muted/20 cursor-pointer"
                    onClick={() => openEditor(t.id)}
                  >
                    <td className="p-3 font-medium text-text">{t.name}</td>
                    <td className="p-3 text-muted">{t.ops_family ?? "—"}</td>
                    <td className="p-3 text-muted">{t.status}</td>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="text-muted hover:text-text inline-flex items-center gap-1"
                        onClick={() => openEditor(t.id)}
                        aria-label="Ouvrir l’éditeur"
                      >
                        <Pencil size={14} />
                        Éditer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
